import { CheckCircle2, Sparkles, User, Wrench, XCircle } from 'lucide-react'
import { useMemo } from 'react'

// Simple markdown renderer for basic formatting
const renderMarkdown = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = []
  let currentIndex = 0
  let keyIndex = 0

  // Combined regex for bold (**text**) and italic (*text*)
  const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)/g
  let match

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > currentIndex) {
      parts.push(text.slice(currentIndex, match.index))
    }

    if (match[1]) {
      // Bold match
      parts.push(<strong key={`b-${keyIndex++}`}>{match[2]}</strong>)
    } else if (match[3]) {
      // Italic match
      parts.push(<em key={`i-${keyIndex++}`}>{match[4]}</em>)
    }

    currentIndex = match.index + match[0].length
  }

  // Add remaining text
  if (currentIndex < text.length) {
    parts.push(text.slice(currentIndex))
  }

  return parts.length > 0 ? parts : [text]
}

type ToolInvocation = {
  toolName: string
  args: Record<string, unknown>
  state: 'partial-call' | 'call' | 'result'
  result?: unknown
}

type Message = {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  toolInvocations?: ToolInvocation[]
}

type ChatMessageProps = {
  message: Message
}

export const ChatMessage = ({ message }: ChatMessageProps) => {
  const isUser = message.role === 'user'

  // Parse tool invocations if present
  const toolInvocations = message.toolInvocations || []

  // Render markdown content for assistant messages
  const renderedContent = useMemo(() => {
    if (!message.content) return null
    if (isUser) return message.content
    return renderMarkdown(message.content)
  }, [message.content, isUser])

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUser
            ? 'bg-gray-200 text-gray-600'
            : 'bg-gradient-to-br from-purple-500 to-indigo-500 text-white'
        }`}
      >
        {isUser ? <User className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
      </div>

      {/* Content */}
      <div className={`flex flex-col max-w-[80%] ${isUser ? 'items-end' : 'items-start'}`}>
        {/* Text content */}
        {renderedContent && (
          <div
            className={`px-4 py-2 rounded-2xl text-sm ${
              isUser
                ? 'bg-purple-600 text-white rounded-tr-sm'
                : 'bg-gray-100 text-gray-800 rounded-tl-sm'
            }`}
          >
            <p className="whitespace-pre-wrap">{renderedContent}</p>
          </div>
        )}

        {/* Tool invocations */}
        {toolInvocations.length > 0 && (
          <div className="mt-2 space-y-1">
            {toolInvocations.map((invocation, index) => (
              <ToolInvocationBadge key={index} invocation={invocation} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

type ToolInvocationBadgeProps = {
  invocation: ToolInvocation
}

const ToolInvocationBadge = ({ invocation }: ToolInvocationBadgeProps) => {
  const { toolName, args, state, result } = invocation

  // Get a human-readable tool name
  const toolLabels: Record<string, string> = {
    getDesignInfo: 'Design info ophalen',
    searchAssets: 'Assets zoeken',
    addText: 'Tekst toevoegen',
    updateTextContent: 'Tekst wijzigen',
    updateTextStyle: 'Stijl wijzigen',
    addAsset: 'Asset toevoegen',
    updateAssetColor: 'Asset kleur wijzigen',
    moveElement: 'Element verplaatsen',
    resizeElement: 'Grootte wijzigen',
    rotateElement: 'Element roteren',
    centerElement: 'Element centreren',
    bringToFront: 'Naar voorgrond',
    sendToBack: 'Naar achtergrond',
    deleteElement: 'Element verwijderen',
    duplicateElement: 'Element kopiëren',
    validateDesign: 'Design valideren',
  }

  const label = toolLabels[toolName] || toolName

  // Parse result for success/failure
  let isSuccess = true
  let resultMessage = ''

  if (state === 'result' && result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result
      isSuccess = parsed.success !== false
      resultMessage = parsed.message || ''
    } catch {
      // If we can't parse, assume success
    }
  }

  // Get preview of args
  const argsPreview = Object.entries(args)
    .slice(0, 2)
    .map(([key, value]) => {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value)
      return `${key}: ${stringValue.slice(0, 20)}${stringValue.length > 20 ? '...' : ''}`
    })
    .join(', ')

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${
        state === 'result'
          ? isSuccess
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
          : 'bg-gray-50 text-gray-600 border border-gray-200'
      }`}
    >
      {state === 'result' ? (
        isSuccess ? (
          <CheckCircle2 className="w-3.5 h-3.5" />
        ) : (
          <XCircle className="w-3.5 h-3.5" />
        )
      ) : (
        <Wrench className="w-3.5 h-3.5 animate-pulse" />
      )}

      <span className="font-medium">{label}</span>

      {argsPreview && state !== 'result' && (
        <span className="text-gray-400 truncate max-w-[150px]">({argsPreview})</span>
      )}

      {resultMessage && state === 'result' && (
        <span className="text-gray-500 truncate max-w-[150px]">- {resultMessage}</span>
      )}
    </div>
  )
}
